// Help page: keyboard shortcuts, icon glossary, and quick how-tos.
// Linked from the top nav so it is reachable from anywhere.
// Icons are rendered inline via <Icon> — no emoji.

import { Icon } from "../ui/icons";
import { Card } from "../ui/Card";
import { t } from "../i18n";

export function Help() {
  return (
    <>
      <h2>{t("help.pageTitle")}</h2>

      {/* ---- Keyboard shortcuts ---- */}
      <Card style={{ marginTop: 16 }}>
        <h3 style={{ marginTop: 0, marginBottom: 12 }}>{t("help.shortcutsHeading")}</h3>
        <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 14 }}>
          <tbody>
            <Shortcut keys={t("help.shortcutUndoKeys")} what={t("help.shortcutUndoDesc")} />
            <Shortcut keys={t("help.shortcutAltKeys")} what={t("help.shortcutAltDesc")} />
            <Shortcut keys={t("help.shortcutEscKeys")} what={t("help.shortcutEscDesc")} />
            <Shortcut keys={t("help.shortcutEnterKeys")} what={t("help.shortcutEnterDesc")} />
          </tbody>
        </table>
      </Card>

      {/* ---- What the buttons mean ---- */}
      <Card style={{ marginTop: 16 }}>
        <h3 style={{ marginTop: 0, marginBottom: 12 }}>{t("help.buttonsHeading")}</h3>

        <IconEntry icon={<Icon name="edit" size={15} />} label={t("help.editParagraphLabel")}>
          {t("help.editParagraphPre")}
          <InlineIcon><Icon name="scissors" size={14} /></InlineIcon>{t("help.editParagraphMid")}
          <InlineIcon><Icon name="bookmark" size={14} /></InlineIcon>{t("help.editParagraphPost")}
        </IconEntry>

        <IconEntry icon={<Icon name="edit" size={15} />} label={t("help.editSpeakerLabel")}>
          {t("help.editSpeakerDesc")}
        </IconEntry>

        <IconEntry icon={<Icon name="bookmark" size={15} />} label={t("help.bookmarkLabel")}>
          {t("help.bookmarkDesc")}
        </IconEntry>

        <IconEntry icon={<span style={{ display: "inline-block", width: 3, alignSelf: "stretch", background: "var(--ink-3)", borderRadius: 2 }} />} label={t("help.dashedLineLabel")}>
          {t("help.dashedLineDesc")}
        </IconEntry>

        <IconEntry icon={<span style={{ fontSize: 12, borderBottom: "1px dotted var(--ink-2)" }}>{t("help.dottedUnderlineWord")}</span>} label={t("help.dottedUnderlineLabel")}>
          {t("help.dottedUnderlineDesc")}
        </IconEntry>

        <IconEntry icon={<Icon name="eye" size={15} />} label={t("help.hideEditsLabel")}>
          {t("help.hideEditsDesc")}
        </IconEntry>

        <IconEntry icon={<Icon name="undo" size={15} />} label={t("help.undoLabel")}>
          {t("help.undoDesc")}
        </IconEntry>

        <IconEntry icon={<Icon name="unlock" size={15} />} label={t("help.lockLabel")}>
          {t("help.lockDesc")}
        </IconEntry>

        <IconEntry icon={<Icon name="refresh" size={15} />} label={t("help.updateAnalysisLabel")}>
          {t("help.updateAnalysisDesc")}
        </IconEntry>

        <IconEntry icon={<Icon name="undo" size={15} />} label={t("help.resetLabel")}>
          {t("help.resetDesc")}
        </IconEntry>

        <IconEntry icon={<Icon name="x" size={15} />} label={t("help.cancelEditLabel")}>
          {t("help.cancelEditDesc")}
        </IconEntry>
      </Card>

      {/* ---- How to split a paragraph ---- */}
      <Card style={{ marginTop: 16 }}>
        <h3 style={{ marginTop: 0, marginBottom: 8 }}>{t("help.splitHeading")}</h3>
        <ol style={{ marginTop: 8, lineHeight: 1.7 }}>
          <li>
            {t("help.splitStep1Pre")}
            <InlineIcon><Icon name="scissors" size={13} /></InlineIcon>{t("help.splitStep1Post")}
          </li>
          <li>{t("help.splitStep2")}</li>
          <li>{t("help.splitStep3")}</li>
          <li>
            {t("help.splitStep4")}
          </li>
        </ol>
      </Card>

      {/* ---- How to fix a wrong word ---- */}
      <Card style={{ marginTop: 16 }}>
        <h3 style={{ marginTop: 0, marginBottom: 8 }}>{t("help.fixWordHeading")}</h3>
        <ol style={{ marginTop: 8, lineHeight: 1.7 }}>
          <li>
            {t("help.fixWordStep1Pre")}
            <InlineIcon><Icon name="edit" size={13} /></InlineIcon>{t("help.fixWordStep1Post")}
          </li>
          <li>{t("help.fixWordStep2")}</li>
          <li>{t("help.fixWordStep3")}</li>
          <li>{t("help.fixWordStep4")}</li>
        </ol>
      </Card>

      {/* ---- Recovery ---- */}
      <Card style={{ marginTop: 16 }}>
        <h3 style={{ marginTop: 0, marginBottom: 8 }}>{t("help.recoveryHeading")}</h3>
        <ul style={{ marginTop: 8, lineHeight: 1.7 }}>
          <li>
            <strong>{t("help.recoverySingleLabel")}</strong>{t("help.recoverySingleMid")}
            <InlineIcon><Icon name="undo" size={13} /></InlineIcon>{" "}
            <strong>{t("help.undoLabel")}</strong>{t("help.recoverySingleTail")}
          </li>
          <li>
            <strong>{t("help.recoveryWholeLabel")}</strong>{t("help.recoveryWholeMid")}
            <strong>{t("common.cancel")}</strong>{t("help.recoveryWholeTail")}
          </li>
          <li>
            <strong>{t("help.recoveryParagraphLabel")}</strong>{t("help.recoveryParagraphMid")}
            <strong>{t("transcriptTab.resetToOriginal")}</strong>{t("help.recoveryParagraphTail")}
          </li>
          <li>
            <strong>{t("help.recoveryForgotLabel")}</strong>{t("help.recoveryForgotMid")}
            <InlineIcon><Icon name="eye" size={13} /></InlineIcon>{" "}
            <strong>{t("help.recoveryForgotHideEdits")}</strong>{t("help.recoveryForgotTail")}
          </li>
        </ul>
      </Card>
    </>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function Shortcut({ keys, what }: { keys: string; what: string }) {
  return (
    <tr>
      <td
        style={{
          padding: "8px 12px 8px 0",
          whiteSpace: "nowrap",
          verticalAlign: "top",
          borderBottom: "1px solid var(--line)",
          width: 1,
        }}
      >
        <kbd
          style={{
            fontFamily: "var(--font-sans)",
            fontSize: 12,
            fontWeight: 600,
            background: "var(--surface-2)",
            border: "1px solid var(--line-2)",
            borderRadius: 4,
            padding: "2px 6px",
            color: "var(--ink-2)",
          }}
        >
          {keys}
        </kbd>
      </td>
      <td
        style={{
          padding: "8px 0",
          color: "var(--ink)",
          borderBottom: "1px solid var(--line)",
        }}
      >
        {what}
      </td>
    </tr>
  );
}

function IconEntry({
  icon,
  label,
  children,
}: {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div style={{ marginTop: 14, display: "flex", gap: 10, alignItems: "flex-start" }}>
      <span
        style={{
          flexShrink: 0,
          width: 28,
          height: 28,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "var(--surface-2)",
          border: "1px solid var(--line)",
          borderRadius: "var(--radius-sm)",
          color: "var(--ink-2)",
          marginTop: 1,
        }}
      >
        {icon}
      </span>
      <div>
        <div style={{ fontWeight: 600, fontSize: 13, color: "var(--ink)", marginBottom: 2 }}>
          {label}
        </div>
        <div style={{ fontSize: 13, color: "var(--ink-2)", lineHeight: 1.5 }}>
          {children}
        </div>
      </div>
    </div>
  );
}

function InlineIcon({ children }: { children: React.ReactNode }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        verticalAlign: "middle",
        marginInline: 2,
        color: "var(--ink-2)",
      }}
    >
      {children}
    </span>
  );
}
